import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronDown, ChevronUp, FileText, Signature } from 'lucide-react';
import { fetchMesDevisSponsor } from '../services/api';

import { dateFr } from '../utils/dates';
const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

const euros = (montant: number) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })
    .format(montant);

const statuts: Record<string, { label: string; classe: string }> = {
  brouillon: {
    label: 'En préparation',
    classe: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
  },
  envoye: {
    label: 'À valider',
    classe: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-200',
  },
  valide: {
    label: 'Validé',
    classe: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-200',
  },
  devis_refuse: {
    label: 'Refusé',
    classe: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
  },
};

/**
 * Devis du sponsor connecté.
 *
 * Un devis en attente reste actionnable : le lien de validation est justement
 * ce que le sponsor a perdu dans sa boîte, et le retrouver ici lui évite
 * d'écrire au bureau.
 *
 * Les fiches tiennent en vignettes, quatre par ligne, repliées : après
 * quelques saisons un sponsor en accumule une dizaine, et une liste de blocs
 * dépliés ferait défiler longtemps pour retrouver celui de l'année.
 */
export default function MesDevisSponsor() {
  const { data, isPending } = useQuery({
    queryKey: ['mes-devis-sponsor'],
    queryFn: fetchMesDevisSponsor,
    retry: false,
  });

  const devis: any[] = data?.devis ?? [];

  if (isPending || devis.length === 0) return null;

  return (
    <section className="space-y-3">
      <h2 className="font-semibold lg:text-lg text-gray-900 dark:text-gray-100 flex items-center gap-2">
        <FileText className="w-5 h-5 text-primary-600" />
        Mes devis
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {devis.map((d) => (
          <CarteDevis key={d.id} devis={d} />
        ))}
      </div>
    </section>
  );
}

function CarteDevis({ devis: d }: { devis: any }) {
  const [ouverte, setOuverte] = useState(false);

  const statut = statuts[d.statut] ?? {
    label: d.statut,
    classe: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
  };

  return (
    <div className="card p-4 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-mono text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
            {d.numero}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{euros(d.montant)}</p>
        </div>

        <button
          type="button"
          onClick={() => setOuverte(!ouverte)}
          aria-expanded={ouverte}
          aria-label={ouverte ? `Replier le devis ${d.numero}` : `Ouvrir le devis ${d.numero}`}
          className="shrink-0 p-1 rounded-lg text-gray-500 hover:text-primary-700 hover:bg-primary-50 dark:text-gray-400 dark:hover:bg-gray-700"
        >
          {ouverte ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      <span className={`inline-block text-xs px-2 py-0.5 rounded-full ${statut.classe}`}>
        {statut.label}
      </span>

      {ouverte && (
        <div className="pt-2 border-t border-gray-100 dark:border-gray-800 space-y-2">
          {d.valideLe && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Validé le {dateFr(d.valideLe)}
            </p>
          )}

          {d.pdfDisponible && (
            <a
              href={`${API_BASE}/sponsor/mes-devis/${d.id}/pdf`}
              className="btn-outline w-full justify-center inline-flex items-center gap-2 text-sm px-3 py-2"
            >
              <FileText className="w-4 h-4" />
              Devis PDF
            </a>
          )}

          {d.lienValidation && (
            <Link
              to={d.lienValidation}
              className="btn-primary w-full justify-center inline-flex items-center gap-2 text-sm px-3 py-2"
            >
              <Signature className="w-4 h-4" />
              Valider et payer
              <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
